import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Building2, Check, User } from "lucide-react";
import { COUNTIES, PRODUCTS, SERVICES } from "@/data/site";
import { COUNTRIES, countryLabel, useLang, useSite } from "@/i18n";
import { trackConversion, trackingPayload } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-ink/20 bg-ink/5 px-4 py-3 text-base font-light text-ink placeholder-ink/40 outline-none transition-colors duration-300 focus:border-ink";

const labelCls = "mb-2 block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/55";

const phoneValid = (p) => {
  const raw = (p || "").replace(/[\s.\-()]/g, "");
  if (!raw) return false;
  let digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+40") || raw.startsWith("0040")) digits = `0${digits.slice(2)}`;
  if (/^07\d{8}$/.test(digits)) return true;
  if (raw.startsWith("+") || raw.startsWith("00")) return /^\d{8,15}$/.test(digits);
  return false;
};

export default function QuoteWizard() {
  const { t, lang } = useLang();
  const site = useSite();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [subject, setSubject] = useState("");
  const [county, setCounty] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneErr, setPhoneErr] = useState(false);
  const [cui, setCui] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyInfo, setCompanyInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [done, setDone] = useState(false);
  const boxRef = useRef(null);
  const [preset, setPreset] = useState("");

  useEffect(() => {
    const onQuote = (e) => {
      const subj = e.detail?.subject || "";
      setSubject(subj);
      setPreset(e.detail?.label || subj);
      setDone(false);
      setStep(type ? 2 : 1);
      boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    window.addEventListener("hm:quote", onQuote);
    return () => window.removeEventListener("hm:quote", onQuote);
  }, [type]);

  const lookup = async () => {
    if (!cui.trim()) return;
    setLookupBusy(true);
    try {
      const { data } = await axios.post(`${API}/company-lookup`, { cui });
      setCompanyName(data.name);
      setCompanyInfo(data.address);
      toast.success("ANAF ✓");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "CUI negăsit");
      setCompanyInfo("");
    } finally {
      setLookupBusy(false);
    }
  };

  const validatePhone = () => {
    const ok = phoneValid(phone);
    setPhoneErr(!ok);
    return ok;
  };

  const canNext =
    (step === 1 && !!type) ||
    (step === 2 && !!subject && !!county && (type !== "company" || !!(cui.trim() && companyName.trim()))) ||
    (step === 3 &&
      name.trim().length >= 2 &&
      email.includes("@") &&
      phoneValid(phone) &&
      (type !== "company" || (cui.trim() && companyName.trim())));

  const submit = async () => {
    if (!validatePhone()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name,
        email,
        phone,
        subject,
        county,
        message: `Cerere ofertă — ${subject}${county ? ` (${county})` : ""}`,
        ...(type === "company" ? { cui, company_name: companyName } : {}),
        ...trackingPayload(lang),
      });
      trackConversion();
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Eroare la trimitere");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(1);
    setType("");
    setSubject("");
    setCounty("");
    setName("");
    setEmail("");
    setPhone("");
    setPhoneErr(false);
    setCui("");
    setCompanyName("");
    setCompanyInfo("");
    setDone(false);
  };

  return (
    <div
      ref={boxRef}
      className="border border-ink/10 bg-base p-7 shadow-xl shadow-black/10"
      data-testid="quote-wizard"
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-heading text-xl font-semibold leading-snug text-ink" data-testid="wizard-title">
          {t("w_title")}
        </h3>
        <span className="shrink-0 font-heading text-sm text-ink/50" data-testid="wizard-step-indicator">
          0{step} / 03
        </span>
      </div>
      {preset && !done && (
        <p className="mt-3 inline-flex items-center gap-2 border border-gold/40 bg-gold/10 px-3 py-1.5 text-[12px] font-semibold text-ink" data-testid="wizard-preset">
          <span className="h-1.5 w-1.5 bg-gold" aria-hidden /> {t("w_preset")}: {preset}
        </p>
      )}

      <div className="mt-4 flex gap-1.5" aria-hidden>
        {[1, 2, 3].map((s) => (
          <span
            key={s}
            className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${s <= step ? "bg-ink" : "bg-ink/15"}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 flex flex-col items-center py-6 text-center"
            data-testid="wizard-success"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-ink/25 bg-ink/5">
              <Check className="h-6 w-6 text-ink" />
            </span>
            <h4 className="mt-5 font-heading text-2xl font-semibold text-ink">{t("w_success_t")}</h4>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink/65">{t("w_success_x")}</p>
            <button
              onClick={reset}
              data-testid="wizard-reset"
              className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/60 transition-colors hover:text-ink"
            >
              {t("w_reset")}
            </button>
          </motion.div>
        ) : (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            {step === 1 && (
              <div className="mt-7">
                <p className="mb-4 font-heading text-lg font-medium text-ink/90">{t("w_step1")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setType("personal")}
                    data-testid="wizard-type-personal"
                    className={`flex flex-col items-center gap-3 rounded-md border p-5 transition-all duration-300 ${
                      type === "personal"
                        ? "border-ink bg-ink text-base"
                        : "border-ink/20 text-ink/75 hover:border-ink/50 hover:text-ink"
                    }`}
                  >
                    <User className="h-5 w-5" strokeWidth={1.5} />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em]">{t("w_personal")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType("company")}
                    data-testid="wizard-type-company"
                    className={`flex flex-col items-center gap-3 rounded-md border p-5 transition-all duration-300 ${
                      type === "company"
                        ? "border-ink bg-ink text-base"
                        : "border-ink/20 text-ink/75 hover:border-ink/50 hover:text-ink"
                    }`}
                  >
                    <Building2 className="h-5 w-5" strokeWidth={1.5} />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em]">{t("w_company")}</span>
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mt-7 space-y-4">
                <p className="font-heading text-lg font-medium text-ink/90">{t("w_step2")}</p>
                <div>
                  <label className={labelCls}>{t("form_subject")}</label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    data-testid="wizard-subject"
                    className={inputCls}
                  >
                    <option value="" className="text-black">{t("form_subject_ph")}</option>
                    <optgroup label={t("nav_services")}>
                      {SERVICES.map((s, i) => (
                        <option key={s.slug} value={s.name} className="text-black">{site.services[i].name}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t("nav_products")}>
                      {PRODUCTS.map((p, i) => (
                        <option key={p.slug} value={p.name} className="text-black">{site.products[i].name}</option>
                      ))}
                    </optgroup>
                    <option value="Altele" className="text-black">{t("other")}</option>
                  </select>
                </div>
                {type === "company" && (
                  <div>
                    <label className={labelCls}>{t("form_cui")}</label>
                    <div className="flex gap-2.5">
                      <input
                        value={cui}
                        onChange={(e) => setCui(e.target.value)}
                        data-testid="wizard-cui-input"
                        placeholder={t("form_cui_ph")}
                        className={inputCls}
                      />
                      {lang === "ro" && (
                        <button
                          type="button"
                          onClick={lookup}
                          disabled={lookupBusy}
                          data-testid="wizard-cui-lookup"
                          className="shrink-0 rounded-md border border-ink/25 px-4 text-[10px] font-semibold uppercase tracking-widest text-ink/80 transition-colors hover:border-ink hover:text-ink disabled:opacity-50"
                        >
                          {lookupBusy ? t("form_lookup_busy") : t("form_lookup")}
                        </button>
                      )}
                    </div>
                    <input
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      data-testid="wizard-company-name"
                      placeholder={t("form_company_ph")}
                      className={`${inputCls} mt-2.5`}
                    />
                    {companyInfo && (
                      <p className="mt-2 text-xs leading-relaxed text-ink/55" data-testid="wizard-company-info">{companyInfo}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className={labelCls}>{t("w_location")}</label>
                  <select
                    value={county}
                    onChange={(e) => setCounty(e.target.value)}
                    data-testid="wizard-county"
                    className={inputCls}
                  >
                    <option value="" className="text-black">{t("w_location_ph")}</option>
                    {lang === "ro" ? (
                      <>
                        <optgroup label={t("w_counties_group")}>
                          {COUNTIES.map((c) => (
                            <option key={c} value={c} className="text-black">{c}</option>
                          ))}
                        </optgroup>
                        <optgroup label={t("w_countries_group")}>
                          {COUNTRIES.filter((c) => c.code !== "RO").map((c) => (
                            <option key={c.code} value={c.ro} className="text-black">{c.ro}</option>
                          ))}
                        </optgroup>
                      </>
                    ) : (
                      COUNTRIES.map((c) => (
                        <option key={c.code} value={c.ro} className="text-black">{countryLabel(c, lang)}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="mt-7 space-y-4">
                <p className="font-heading text-lg font-medium text-ink/90">{t("w_step3")}</p>
                <div>
                  <label className={labelCls}>
                    {type === "company" ? t("w_rep") : t("form_name")}
                  </label>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="wizard-name" placeholder={t("form_name_ph")} className={inputCls} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t("form_email")}</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="wizard-email" placeholder={t("form_email_ph")} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>{t("form_phone")} *</label>
                    <input
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); setPhoneErr(false); }}
                      onBlur={() => phone && validatePhone()}
                      data-testid="wizard-phone"
                      placeholder={t("phone_ph")}
                      className={`${inputCls} ${phoneErr ? "border-red-400" : ""}`}
                    />
                    {phoneErr && (
                      <p className="mt-1.5 text-[11px] font-medium text-red-300" data-testid="wizard-phone-error">
                        {t("w_phone_error")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-7 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  data-testid="wizard-back"
                  className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/55 transition-colors hover:text-ink"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> {t("w_back")}
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                disabled={!canNext || busy}
                onClick={() => (step < 3 ? setStep(step + 1) : submit())}
                data-testid="wizard-next"
                className="group flex items-center gap-2 bg-ink px-6 py-3 text-[12px] font-semibold uppercase tracking-widest text-base transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? t("form_sending") : step < 3 ? t("w_next") : t("w_send")}
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
